import {ChangeDetectionStrategy, Component, Inject, Input, OnInit, Output, EventEmitter, forwardRef, Optional} from '@angular/core';
import {FormGroupDirective} from '@angular/forms';
import {TranslateService} from '@ngx-translate/core';
import {TRANSLATION_BASE} from 'app/constants/injection-tokens';
import {faInfoCircle} from '@fortawesome/free-solid-svg-icons';

import {DiFormElement} from 'app/common/di-forms/shared/di-form-element';
import {DiFormConfigDirective} from 'app/common/di-forms/directives/di-form.directive';
import {DiCheckboxParams} from 'app/common/di-forms/shared/di-checkbox-params';
import {RoleService} from 'app/blocks/service/api/role.service';
import {LogService} from 'app/blocks/service/log.service';
import {debounce} from 'lodash';

export const formElementProvider: any = {
    provide: DiFormElement,
    useExisting: forwardRef(() => DiCheckboxComponent)
};

@Component({
    selector: 'di-checkbox',
    template: `
        <nz-form-item [formGroup]="thisFormGroup" style="width: 100%; display:inherit; margin-bottom:0px;" *ngIf="this.showField">
            <label
                id="{{ 'di-checkbox-' + field + idSuffix }}"
                nz-checkbox
                formControlName="{{ field }}"
                (ngModelChange)="onChange($event)"
                [nzIndeterminate]="checkboxParams.indeterminate"
                [nzDisabled]="readonly">
                <nz-form-label *ngIf="!suppressLabel" [nzFor]="fieldLabel$ | async" [nzRequired]="fieldIsRequired" [nzNoColon]="true">
                    <span [ngClass]="field">
                        {{ label || (fieldLabel$ | async) }}
                        <ng-container *ngIf="fieldHint$ | async as hint"
                            ><fa-icon class="di-mhint-icon" [icon]="faInfoIcon" size="xs" nz-tooltip [nzTooltipTitle]="hint"></fa-icon
                            ><span class="di-mfield-hint">{{ hint }}</span></ng-container
                        >
                    </span>
                </nz-form-label>
            </label>
        </nz-form-item>
    `,
    providers: [formElementProvider],
    styleUrls: [`./di-checkbox.component.scss`],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class DiCheckboxComponent extends DiFormElement implements OnInit {
    @Input() field: string;
    @Input() idSuffix: string | number = '';
    @Input() suppressLabel = false;
    @Input() readonly = false;
    @Input() checkboxParams: DiCheckboxParams = {
        labelPosition: 'after',
        indeterminate: false
    };
    @Input() roleDependent = false;
    @Input() showField = true;
    @Input() roleSettingSuffix = '';
    @Input() enableTracking = false;
    @Input() label: string;

    faInfoIcon = faInfoCircle;

    @Output() valueChange: EventEmitter<any> = new EventEmitter<any>();

    constructor(
        @Inject(TRANSLATION_BASE) translationBase,
        translateService: TranslateService,
        fgDirective: FormGroupDirective,
        @Optional() diForm: DiFormConfigDirective,
        private _logService: LogService,
        protected roleService: RoleService
    ) {
        super(translationBase, translateService, fgDirective, roleService, diForm);
    }

    getFieldName(): string {
        return this.field;
    }

    private debouncedLog = debounce((value) => {
        this._logService.log('Checkbox Change', {
            field: this._fieldPath,
            value: value
        });
    }, 500);

    onChange($event): void {
        this.valueChange.emit($event);
        if (this.enableTracking) {
            this.debouncedLog($event);
        }
    }
}
